//import { memo } from "react";
import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import cn from "classnames";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { StyledContainer } from "@/src/component/partial/__module__/StyledContainer";
import { VideoPlayer } from "@/src/component/partial/VideoPlayer";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiCollectionType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import { ImageBreakpointModule } from "../ImageBreakpointModule";
import SectionModuleElement from "./SectionModuleElement";

import styles from "./SectionModule.module.scss";

interface SectionModuleProps {
  section: StrapiCollectionType.DynamicPageSection<any>;
  tag?: "section" | "footer";
  lazy?: "lazy" | "eager";
  slug: string;
  classes?: {
    section?: string;
    styledContainer?: string;
    children?: string;
  };
  stylePrefix: string;
  styleContainerId: number | string;
  snapToGrid?: boolean;
  AfterChildComponent?: JSX.Element | JSX.Element[];
  bottomDrawerHeight?: number;
  searchParams?: Record<string, string | string[] | undefined>;
  mobileHint?: boolean;
  deviceWidthHint?: number;
  disableBackgroundElements?: boolean;
  quality: number;
  elevateHeaderGradient?: boolean;
  layout?: "main" | "alternate" | "split" | "none";
}

function SectionModule({
  children,
  tag = "section",
  lazy = "lazy",
  slug,
  section,
  stylePrefix,
  styleContainerId,
  classes,
  snapToGrid,
  AfterChildComponent,
  mobileHint,
  deviceWidthHint,
  disableBackgroundElements,
  quality,
  elevateHeaderGradient,
  layout,
}: PropsWithChildren<SectionModuleProps>) {
  const [resized, setResized] = useState<boolean>(false);
  // mobile hint use UserAgentHelper.ts
  // will return 600 for iPhone/Android/Mobile user agents
  const isMobile = (deviceWidthHint ?? 1920) < 660;

  useEffect(() => {
    if (!section?.background?.video?.data?.attributes?.url || !section?.background?.videoSmallScreen?.data?.attributes?.url) {
      return;
    }

    function handleResizeChange() {
      setResized(true);
      window.removeEventListener("resize", handleResizeChange);
    }

    window.addEventListener("resize", handleResizeChange, { passive: true });
    return () => window.removeEventListener("resize", handleResizeChange);
  }, [section, lazy]);

  function getGradient() {
    switch (section?.background?.linearGradientOverlay ?? "default") {
      case "overlayLinearGradient0":
        return styles.overlayLinearGradient0;
      case "overlayLinearGradient1":
        return styles.overlayLinearGradient1;
      case "overlayLinearGradient2":
        return styles.overlayLinearGradient2;
      case "overlayLinearGradient3":
        return styles.overlayLinearGradient3;
      case "overlayLinearGradient4":
        return styles.overlayLinearGradient4;
      default:
        return;
    }
  }

  function getVideo() {
    if (isMobile && !resized) {
      return section?.background?.videoSmallScreen?.data?.attributes?.url ? section?.background?.videoSmallScreen : section?.background?.video;
    }
    return section?.background?.video;
  }

  function getPoster() {
    if (isMobile && !resized) {
      return section?.background?.assetSmallScreen?.data?.attributes?.url ?? section?.background?.asset?.data?.attributes?.url;
    }
    return section?.background?.asset?.data?.attributes?.url;
  }

  return (
    <SectionModuleElement
      classes={{
        className: classes?.section ? classes?.section : undefined,
        section: cn(styles.section, snapToGrid ? styles.snapToGrid : undefined),
      }}
      style={
        snapToGrid
          ? undefined
          : {
              minHeight: section.minHeight,
              maxHeight: section.maxHeight,
              backgroundColor: CMSLogic.colorSelector(section.background?.color),
            }
      }
      id={`sectionId_${section.sectionId}`}
      tag={tag ?? "section"}
      considerLazy={true}
      lazy={lazy}
    >
      {typeof section?.header?.headerBackgroundShadow === "string" && (
        <div
          className={cn(
            styles.headerBackground,
            styles?.[section?.header?.headerBackgroundShadow],
            elevateHeaderGradient ? styles.elevateHeaderGradient : undefined
          )}
        />
      )}
      <SectionModuleElement
        classes={{
          className: classes?.children ? classes?.children : undefined,
          section: cn(styles.children, snapToGrid ? styles.snapToGrid : undefined),
        }}
      >
        <StyledContainer
          className={cn(snapToGrid ? styles.snapToGrid : undefined, classes?.styledContainer)}
          container={section.container}
          stylePrefix={stylePrefix}
          id={styleContainerId}
          layout={layout}
        >
          {children}
        </StyledContainer>
      </SectionModuleElement>
      {AfterChildComponent}
      {!disableBackgroundElements && (
        <>
          {getVideo() && (
            <VideoPlayer
              videoId={`${slug}.${section.sectionId}.SectionModule.video`}
              asset={getVideo()}
              muted={true}
              play={true}
              controls={false}
              lazy={lazy ?? "eager"}
              classes={{
                video: styles.video,
              }}
              poster={LoadFromCDN.imageSizeAndQuality(getPoster(), 90, deviceWidthHint ?? 1200)}
            />
          )}
          {getPoster() && !getVideo() && (
            <ImgBgResponsive
              className={styles.background}
              imageUrl={LoadFromCDN.imageSizeAndQuality(getPoster(), 95, deviceWidthHint ?? 1920)}
              lazy={lazy}
              prefetch={lazy === "eager"}
            />
          )}
          {section?.background?.imageBreakpoints && (
            <ImageBreakpointModule
              images={section?.background?.imageBreakpoints}
              preload={lazy === "eager"}
              classes={{
                container: styles.backgroundBreakpoints,
              }}
              id={`SM_${section.sectionId}`.replaceAll(".", "_")}
              mobileHint={mobileHint}
              quality={quality}
              lazy={lazy}
            />
          )}
          {section?.background?.linearGradientOverlay && <div className={cn(styles.backgroundGradient, getGradient())} />}
          {section?.background?.color && (
            <div
              className={styles.backgroundColor}
              style={{
                backgroundColor: CMSLogic.colorSelector(section.background.color),
              }}
            />
          )}
        </>
      )}
    </SectionModuleElement>
  );
}

//const MemorizedComponent = memo(SectionModule);
//export default MemorizedComponent;
export default SectionModule;
